import {EventSmall, AgendaEventStatus} from '@/features/agenda/types'
import {QUERY_KEYS, queryClient} from '@/queryClient'
import {socket} from '@/socket'
import {useEffect} from 'react'
import dayjs from 'dayjs'

type QueryData = EventSmall[] | undefined

/**
 * Extracts therapist IDs from an event
 */
const getTherapistIds = (event: EventSmall): string[] => {
    const consultantIds = event.eventConsultants?.map(consultant => consultant.userId) || []
    const teamIds = event.eventTeams?.map(team => team.member.userId) || []
    const substituteIds =
        event.eventConsultants?.map(consultant => consultant.substituteId).filter((id): id is string => !!id) || []

    return [...consultantIds, ...teamIds, ...substituteIds]
}

/**
 * Extracts patient IDs from an event
 */
const getPatientIds = (event: EventSmall): string[] => {
    return [...(event.eventPatients?.map(patient => patient.patientId) || [])]
}

/**
 * Updates React Query cache for a specific query key with new event data
 */
const updateQueryCache = (queryKey: unknown[], updateFn: (oldData: QueryData) => QueryData): void => {
    const existingQueries = queryClient.getQueriesData({queryKey})
    if (existingQueries && existingQueries.length > 0) {
        queryClient.setQueriesData({queryKey}, updateFn)
    }
}

/**
 * Computes an event's [start, end] datetime interval from its date/time/duration fields.
 * When the event has no time it is treated as a full day; when it has no duration it is treated as
 * lasting until the end of its day.
 */
const getEventInterval = (event: EventSmall): {start: dayjs.Dayjs; end: dayjs.Dayjs} | null => {
    let start = dayjs(event.date)
    if (!start.isValid()) return null

    if (event.time) {
        const [hours, minutes] = event.time.split(':').map(Number)
        start = start.hour(hours).minute(minutes).second(0).millisecond(0)
    } else {
        start = start.startOf('day')
    }

    const end = event.duration ? start.add(event.duration, 'minutes') : start.endOf('day')

    return {start, end}
}

/**
 * Checks whether an event's date interval overlaps the query's [startDate, endDate] window.
 * Returns true (permissive) when the range or the event date is missing/unparseable, so an event is
 * never dropped on date grounds when we cannot make a confident decision.
 */
const eventOverlapsDateRange = (event: EventSmall, startDate?: string, endDate?: string): boolean => {
    if (!startDate || !endDate) return true

    const rangeStart = dayjs(startDate).startOf('day')
    const rangeEnd = dayjs(endDate).endOf('day')
    if (!rangeStart.isValid() || !rangeEnd.isValid()) return true

    const interval = getEventInterval(event)
    if (!interval) return true

    // Overlap: the event starts on/before the window end AND ends on/after the window start.
    return !interval.start.isAfter(rangeEnd) && !interval.end.isBefore(rangeStart)
}

/**
 * Checks if an event should be included in a query based on the query filters
 */
const shouldEventBeInQuery = (event: EventSmall, queryKey: unknown[]): boolean => {
    if (!Array.isArray(queryKey) || queryKey.length < 2) return false

    const filters = queryKey[1] as {
        therapists?: string[]
        patients?: string[]
        startDate?: string
        endDate?: string
        [key: string]: unknown
    }

    if (!filters || typeof filters !== 'object') return false

    const eventTherapistIds = getTherapistIds(event)
    const eventPatientIds = getPatientIds(event)

    // Check if event matches therapist filters (only if there are actual therapist filters)
    if (filters.therapists && Array.isArray(filters.therapists) && filters.therapists.length > 0) {
        const hasMatchingTherapist = filters.therapists.some((id: string) => eventTherapistIds.includes(id))
        if (!hasMatchingTherapist) return false
    }

    // Check if event matches patient filters (only if there are actual patient filters)
    if (filters.patients && Array.isArray(filters.patients) && filters.patients.length > 0) {
        const hasMatchingPatient = filters.patients.some((id: string) => eventPatientIds.includes(id))
        if (!hasMatchingPatient) return false
    }

    // Check if the event's date interval overlaps the query's date window, so a created/updated event is
    // only inserted into caches whose visible window actually contains it and never leaks into other
    // weeks/months cached for the same therapist or patient.
    if (!eventOverlapsDateRange(event, filters.startDate, filters.endDate)) return false

    return true
}

/**
 * Updates all relevant caches for an event by checking which queries should contain it
 */
const updateAllRelevantCaches = (event: EventSmall, updateFn: (oldData: QueryData) => QueryData): void => {
    // Get all agenda event queries
    const allQueries = queryClient.getQueriesData({queryKey: [QUERY_KEYS.AGENDA_EVENTS]})

    for (const [queryKey] of allQueries) {
        if (Array.isArray(queryKey)) {
            const shouldInclude = shouldEventBeInQuery(event, queryKey)

            if (shouldInclude) {
                queryClient.setQueriesData({queryKey}, updateFn)
            }
        }
    }
}

/**
 * Updates all relevant caches for an array of IDs with specific entity type
 */
const updateCachesForIds = (
    ids: string[],
    entityType: 'therapists' | 'patients',
    updateFn: (oldData: QueryData) => QueryData
): void => {
    for (const id of ids) {
        const queryKey = [QUERY_KEYS.AGENDA_EVENTS, {[entityType]: [id]}]
        updateQueryCache(queryKey, updateFn)
    }
}

/**
 * Processes consultant absence events by canceling all therapist events in the absence period
 */
const handleConsultantAbsence = (event: EventSmall): void => {
    if (!event.eventConsultants || event.eventConsultants.length === 0 || !event.date || !event.duration) {
        return
    }

    const therapistId = event.eventConsultants[0].userId
    const absenceDate = dayjs(event.date)
    const absenceEndDate = event.duration ? absenceDate.add(event.duration, 'minutes') : absenceDate.endOf('day')

    // Get all agenda event queries that might contain affected events
    const allQueries = queryClient.getQueriesData({queryKey: [QUERY_KEYS.AGENDA_EVENTS]})

    for (const [queryKey] of allQueries) {
        if (Array.isArray(queryKey) && queryKey.length > 0) {
            queryClient.setQueriesData({queryKey}, (oldData: QueryData) => {
                if (!oldData || !Array.isArray(oldData)) return oldData

                // First, add the absence event to the cache if it doesn't already exist
                let updatedEvents = oldData.some(e => e.id === event.id) ? oldData : [...oldData, event]

                // Then update the status of all events for this therapist within the absence period
                updatedEvents = updatedEvents.map(existingEvent => {
                    // Skip non-event types or already cancelled events or completed events
                    if (
                        existingEvent.type !== 'event' ||
                        (existingEvent.status !== 'scheduled' && existingEvent.status !== 'to_be_confirmed')
                    ) {
                        return existingEvent
                    }

                    // Check if the event falls within the absence period
                    const eventDate = dayjs(existingEvent.date)

                    if (existingEvent.time) {
                        const [hours, minutes] = existingEvent.time.split(':').map(Number)
                        eventDate.hour(hours).minute(minutes)
                    } else {
                        eventDate.startOf('day')
                    }

                    const eventEndDate = existingEvent.duration
                        ? eventDate.add(existingEvent.duration, 'minutes')
                        : eventDate.endOf('day')

                    // If event is within absence period and involves this therapist, mark it as to_reschedule
                    if (
                        (eventDate.isAfter(absenceDate) || eventDate.isSame(absenceDate)) &&
                        (eventEndDate.isBefore(absenceEndDate) || eventEndDate.isSame(absenceEndDate)) &&
                        getTherapistIds(existingEvent).includes(therapistId)
                    ) {
                        return {
                            ...existingEvent,
                            status: 'to_reschedule' as AgendaEventStatus
                        }
                    }

                    return existingEvent
                })

                return updatedEvents
            })
        }
    }
}

/**
 * Processes patient absence events by marking all patient events in the absence period as withdrawn
 */
const handlePatientAbsence = (event: EventSmall): void => {
    if (!event.eventPatients || event.eventPatients.length === 0 || !event.date) {
        return
    }

    const patientId = event.eventPatients[0].patientId
    const absenceDate = dayjs(event.date)
    const absenceEndDate = absenceDate.endOf('day')

    // Get all agenda event queries that might contain affected events
    const allQueries = queryClient.getQueriesData({queryKey: [QUERY_KEYS.AGENDA_EVENTS]})

    for (const [queryKey] of allQueries) {
        if (Array.isArray(queryKey) && queryKey.length > 0) {
            queryClient.setQueriesData({queryKey}, (oldData: QueryData) => {
                if (!oldData || !Array.isArray(oldData)) return oldData

                // First, add the absence event to the cache if it doesn't already exist
                let updatedEvents = oldData.some(e => e.id === event.id) ? oldData : [...oldData, event]

                // Then update the status of all events for this patient within the absence period
                updatedEvents = updatedEvents.map(existingEvent => {
                    // Skip non-event types or already withdrawn events or completed events
                    if (
                        existingEvent.type !== 'event' ||
                        (existingEvent.status !== 'scheduled' && existingEvent.status !== 'to_be_confirmed')
                    ) {
                        return existingEvent
                    }

                    // Check if the event falls within the absence period
                    const eventDate = dayjs(existingEvent.date)

                    if (existingEvent.time) {
                        const [hours, minutes] = existingEvent.time.split(':').map(Number)
                        eventDate.hour(hours).minute(minutes)
                    } else {
                        eventDate.startOf('day')
                    }

                    const eventEndDate = existingEvent.duration
                        ? eventDate.add(existingEvent.duration, 'minutes')
                        : eventDate.endOf('day')

                    // If event is within absence period and involves this patient, mark it as withdrawn
                    if (
                        (eventDate.isAfter(absenceDate) || eventDate.isSame(absenceDate)) &&
                        (eventEndDate.isBefore(absenceEndDate) || eventEndDate.isSame(absenceEndDate)) &&
                        getPatientIds(existingEvent).includes(patientId)
                    ) {
                        return {
                            ...existingEvent,
                            status: 'withdrawn' as AgendaEventStatus
                        }
                    }

                    return existingEvent
                })

                return updatedEvents
            })
        }
    }
}

export const useSocketAgendaEvents = () => {
    useEffect(() => {
        const handleEventCreated = (data: {events: EventSmall[]}) => {
            for (const event of data.events) {
                // Check if this is a consultant absence event to remove all the events between the start and end date of the absence
                if (event.type === 'consultant_absence') {
                    handleConsultantAbsence(event)
                }

                // Check if this is a patient absence event to remove all the events between the start and end date of the absence
                if (event.type === 'patient_absence') {
                    handlePatientAbsence(event)
                }

                // Update all relevant caches using the new comprehensive approach
                updateAllRelevantCaches(event, (oldData: QueryData) => {
                    if (!oldData || !Array.isArray(oldData)) return oldData

                    // Add only this specific event to the cache if it doesn't already exist
                    const updatedEvents = oldData.some(e => e.id === event.id) ? oldData : [...oldData, event]
                    return updatedEvents
                })
            }
        }

        const handleEventUpdated = (data: {events: EventSmall[]}) => {
            for (const event of data.events) {
                // First, find the existing event in any cache to check for substitute changes
                const allQueries = queryClient.getQueriesData({queryKey: [QUERY_KEYS.AGENDA_EVENTS]})

                // Look for the previous event data with the same ID
                for (const [queryKey, queryData] of allQueries) {
                    if (Array.isArray(queryKey) && queryKey.length > 0 && queryData) {
                        const typedQueryData = queryData as QueryData
                        if (!typedQueryData || !Array.isArray(typedQueryData)) continue

                        const existingEvent = typedQueryData.find(e => e.id === event.id)
                        if (existingEvent && existingEvent.eventConsultants) {
                            // Extract previous substitute IDs
                            const oldSubstituteIds = existingEvent.eventConsultants
                                .map(consultant => consultant.substituteId)
                                .filter((id): id is string => id !== null && id !== undefined)

                            // Extract new substitute IDs
                            const newSubstituteIds =
                                event.eventConsultants
                                    ?.map(consultant => consultant.substituteId)
                                    .filter((id): id is string => id !== null && id !== undefined) || []

                            // Find IDs that were in old but not in new (removed substitutes)
                            const removedSubstituteIds = oldSubstituteIds.filter(id => !newSubstituteIds.includes(id))

                            if (removedSubstituteIds.length > 0) {
                                // Remove the event from the caches of removed substitutes
                                for (const substituteId of removedSubstituteIds) {
                                    updateCachesForIds([substituteId], 'therapists', (oldData: QueryData) => {
                                        if (!oldData) return oldData
                                        return oldData.filter(e => e.id !== event.id)
                                    })
                                }
                            }

                            break
                        }
                    }
                }

                // Update all relevant caches using the new comprehensive approach
                updateAllRelevantCaches(event, (oldData: QueryData) => {
                    if (!oldData || !Array.isArray(oldData)) return oldData

                    const updatedEvents = oldData.some(e => e.id === event.id)
                        ? oldData.map(e => (e.id === event.id ? event : e))
                        : [...oldData, event]

                    return updatedEvents
                })
            }
        }

        const handleEventDeleted = (data: {events: string[]}) => {
            // Remove the deleted events from all caches
            queryClient.setQueriesData({queryKey: [QUERY_KEYS.AGENDA_EVENTS]}, (oldData: QueryData) => {
                if (!oldData || !Array.isArray(oldData)) return oldData
                const filteredEvents = oldData.filter(e => !data.events.includes(e.id))
                return filteredEvents
            })
        }

        socket.on('events:created', handleEventCreated)
        socket.on('events:updated', handleEventUpdated)
        socket.on('events:deleted', handleEventDeleted)

        return () => {
            socket.off('events:created', handleEventCreated)
            socket.off('events:updated', handleEventUpdated)
            socket.off('events:deleted', handleEventDeleted)
        }
    }, [])
}
